import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { suppressionsApi } from '@/lib/suppressionsApi';
import { useToast } from '@/components/ui';

/**
 * Settings → Do-not-contact list (deliverability guardrails, W3). Reps can see
 * everyone who has unsubscribed / bounced / been manually blocked, and add or
 * remove addresses. Sequences consult this list before every send.
 */
export function SuppressionSection({ wsId }: { wsId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [email, setEmail] = useState('');

  const list = useQuery({
    queryKey: ['suppressions', wsId],
    queryFn: () => suppressionsApi.list(wsId),
    enabled: !!wsId,
  });

  const add = useMutation({
    mutationFn: () => suppressionsApi.add(wsId, email.trim()),
    onSuccess: () => {
      setEmail('');
      qc.invalidateQueries({ queryKey: ['suppressions', wsId] });
      toast.success(t('settings.suppress_added'));
    },
    onError: () => toast.error(t('settings.suppress_add_failed')),
  });

  const remove = useMutation({
    mutationFn: (id: string) => suppressionsApi.remove(wsId, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['suppressions', wsId] });
      toast.success(t('settings.suppress_removed'));
    },
    onError: () => toast.error(t('common.delete_failed')),
  });

  const rows = list.data ?? [];
  const valid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());

  return (
    <section className="rounded-2xl bg-white border border-ink-200 p-5">
      <h2 className="text-sm font-semibold text-ink-900 mb-1">{t('settings.suppress_section')}</h2>
      <p className="text-xs text-ink-500 mb-3">{t('settings.suppress_intro')}</p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (valid && !add.isPending) add.mutate();
        }}
        className="flex items-center gap-2 mb-4"
      >
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('settings.suppress_add_placeholder')}
          className="flex-1 rounded-md border border-ink-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
        />
        <button
          type="submit"
          disabled={!valid || add.isPending}
          className="shrink-0 rounded-md bg-ink-900 px-3.5 py-1.5 text-sm font-semibold text-white hover:bg-ink-800 disabled:opacity-50"
        >
          {t('settings.suppress_add')}
        </button>
      </form>

      {list.isLoading ? (
        <p className="text-xs text-ink-500">{t('common.loading')}</p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-ink-500">{t('settings.suppress_empty')}</p>
      ) : (
        <ul className="divide-y divide-ink-100 rounded-md border border-ink-200">
          {rows.map((r) => (
            <li key={r.id} className="flex items-center gap-3 px-3 py-2">
              <span className="flex-1 min-w-0 text-sm text-ink-900 truncate">{r.email}</span>
              <span className="shrink-0 text-[10px] uppercase tracking-wide rounded-full bg-ink-100 text-ink-600 px-2 py-0.5">
                {r.reason}
              </span>
              <button
                onClick={() => remove.mutate(r.id)}
                disabled={remove.isPending}
                aria-label={t('common.delete')}
                title={t('common.delete')}
                className="shrink-0 rounded-md p-1.5 text-ink-400 hover:bg-warn-500/10 hover:text-warn-600 disabled:opacity-50"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
